import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { purchasesAPI } from '../../api';
import Layout from '../../components/Layout';
import SlideOver from '../../components/ui/SlideOver';
import PurchasePaymentForm from '../../components/purchases/PurchasePaymentForm';
import BulkPurchasePaymentModal from '../../components/purchases/BulkPurchasePaymentModal';
import { Tooltip, TooltipProvider } from '../../components/ui/Tooltip';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Card, CardContent } from '../../components/ui/Card';
import { cn } from '../../lib/utils';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { FormLabel } from '../../components/ui/FormLabel';
import { Eye, Edit, Archive, ArchiveRestore, Trash2, ChevronDown, Copy, CreditCard, Filter, Paperclip, Banknote, Wallet } from 'lucide-react';

const Purchases = () => {
  const navigate = useNavigate();
  
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('active'); // 'active' | 'archived' | 'deleted'
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const dropdownRefs = useRef({});
  const dropdownMenuRef = useRef(null); // portal menu - clicks inside must not trigger close
  const [deleteModalPurchase, setDeleteModalPurchase] = useState(null);
  const [confirmDeletePurchase, setConfirmDeletePurchase] = useState(null); // { purchase, disposition? } for final confirm
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [paymentModalPurchase, setPaymentModalPurchase] = useState(null); // purchase to log payment for
  const [duplicatingPurchaseId, setDuplicatingPurchaseId] = useState(null);
  const [selectedPurchaseIds, setSelectedPurchaseIds] = useState(new Set());
  const [bulkActionInProgress, setBulkActionInProgress] = useState(false);
  const [showBulkPaymentModal, setShowBulkPaymentModal] = useState(false);

  const toggleSelect = (id) => {
    setSelectedPurchaseIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedPurchaseIds.size >= purchases.length) {
      setSelectedPurchaseIds(new Set());
    } else {
      setSelectedPurchaseIds(new Set(purchases.map((p) => p.id)));
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      const activeRef = openDropdownId ? dropdownRefs.current[openDropdownId] : null;
      const clickedInsideMenu = dropdownMenuRef.current?.contains(e.target);
      if (activeRef && !activeRef.contains(e.target) && !clickedInsideMenu) {
        setOpenDropdownId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownId]);

  // Position dropdown for portal; close on scroll so it doesn't drift
  useEffect(() => {
    if (!openDropdownId) {
      setDropdownPosition(null);
      return;
    }
    const el = dropdownRefs.current[openDropdownId];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setDropdownPosition({ top: rect.bottom + 4, left: rect.left });

    const closeOnScroll = () => setOpenDropdownId(null);
    window.addEventListener('scroll', closeOnScroll, true);
    window.addEventListener('resize', closeOnScroll);
    return () => {
      window.removeEventListener('scroll', closeOnScroll, true);
      window.removeEventListener('resize', closeOnScroll);
    };
  }, [openDropdownId]);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  useEffect(() => {
    fetchPurchases();
  }, [pagination.page, search, statusFilter, supplierFilter, visibilityFilter]);

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      setError('');
      
      const isDeleted = visibilityFilter === 'deleted';
      const response = await purchasesAPI.getAll({
        page: pagination.page,
        limit: pagination.limit,
        search,
        status: statusFilter || undefined,
        supplier_id: supplierFilter || undefined,
        isArchived: isDeleted ? undefined : visibilityFilter === 'archived',
        isDeleted: isDeleted,
      });
      
      setPurchases(response.purchases);
      setPagination(prev => ({
        ...prev,
        ...response.pagination
      }));
    } catch (err) {
      setError(err.message || 'Failed to load purchases');
      console.error('Error fetching purchases:', err);
    } finally {
      setLoading(false);
    }
  };

  const performDelete = async (purchaseId, paymentDisposition = null) => {
    try {
      await purchasesAPI.delete(purchaseId, paymentDisposition ? { paymentDisposition } : {});
      fetchPurchases();
    } catch (err) {
      alert(err.message || 'Failed to delete purchase');
      console.error('Delete error:', err);
    } finally {
      setDeleteInProgress(false);
      setDeleteModalPurchase(null);
      setConfirmDeletePurchase(null);
    }
  };

  const handleDeletePurchase = (purchase) => {
    setOpenDropdownId(null);
    const hasPayments = (purchase.amountPaid ?? 0) > 0;

    if (!hasPayments) {
      setConfirmDeletePurchase({ purchase, disposition: null });
      return;
    }

    setDeleteModalPurchase(purchase);
  };

  const handlePaymentDispositionChoice = (disposition) => {
    if (!deleteModalPurchase) return;
    setConfirmDeletePurchase({ purchase: deleteModalPurchase, disposition });
    setDeleteModalPurchase(null);
  };

  const handleConfirmDelete = () => {
    if (!confirmDeletePurchase) return;
    const { purchase, disposition } = confirmDeletePurchase;
    setConfirmDeletePurchase(null);
    setDeleteInProgress(true);
    performDelete(purchase.id, disposition ?? undefined);
  };

  const handleArchivePurchase = async (purchaseId, isArchived) => {
    try {
      await purchasesAPI.archive(purchaseId, !isArchived);
      fetchPurchases();
    } catch (err) {
      alert(err.message || 'Failed to update purchase');
      console.error('Archive error:', err);
    }
  };

  const handleMarkReceived = async (purchaseId) => {
    if (!confirm('Mark this purchase as received?')) return;

    try {
      await purchasesAPI.markReceived(purchaseId);
      fetchPurchases();
    } catch (err) {
      alert(err.message || 'Failed to update purchase');
      console.error('Mark received error:', err);
    }
  };

  const handleDuplicate = async (purchase) => {
    setOpenDropdownId(null);
    try {
      setDuplicatingPurchaseId(purchase.id);
      const { purchase: created } = await purchasesAPI.duplicate(purchase.id);
      const newId = created?.id;
      if (newId) {
        navigate(`/tenant/purchases/${newId}`);
      } else {
        alert('Duplicate created but could not redirect.');
      }
    } catch (err) {
      alert(err.message || 'Failed to duplicate purchase');
      console.error('Duplicate purchase error:', err);
    } finally {
      setDuplicatingPurchaseId(null);
    }
  };

  const handleFilterOnSupplier = (supplierId) => {
    setOpenDropdownId(null);
    setSupplierFilter(supplierId);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleLogPayment = (purchase) => {
    setOpenDropdownId(null);
    setPaymentModalPurchase(purchase);
  };

  const handlePaymentRecorded = () => {
    setPaymentModalPurchase(null);
    fetchPurchases();
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedPurchaseIds);
    if (ids.length === 0) return;
    if (!confirm(`Delete ${ids.length} selected purchase(s)? This will soft-delete them.`)) return;
    setBulkActionInProgress(true);
    try {
      await Promise.all(ids.map((id) => purchasesAPI.delete(id)));
      setSelectedPurchaseIds(new Set());
      fetchPurchases();
    } catch (err) {
      alert(err.message || 'Failed to delete some purchases');
    } finally {
      setBulkActionInProgress(false);
    }
  };

  const handleOpenBulkPaymentModal = () => {
    const ids = Array.from(selectedPurchaseIds);
    if (ids.length === 0) return;
    setShowBulkPaymentModal(true);
  };

  const handleBulkPaymentSuccess = () => {
    setShowBulkPaymentModal(false);
    setSelectedPurchaseIds(new Set());
    fetchPurchases();
  };

  const handleBulkPaymentCancel = () => {
    setShowBulkPaymentModal(false);
  };

  const handleBulkArchive = async () => {
    const ids = Array.from(selectedPurchaseIds);
    if (ids.length === 0) return;
    if (!confirm(`Archive ${ids.length} selected purchase(s)?`)) return;
    setBulkActionInProgress(true);
    try {
      await Promise.all(ids.map((id) => purchasesAPI.archive(id, true)));
      setSelectedPurchaseIds(new Set());
      fetchPurchases();
    } catch (err) {
      alert(err.message || 'Failed to archive some purchases');
    } finally {
      setBulkActionInProgress(false);
    }
  };

  const getStatusTextColor = (status) => {
    const colors = {
      pending: 'text-[var(--ds-status-warning)]',
      received: 'text-[var(--ds-text-secondary)]',
      'part-paid': 'text-[var(--ds-status-warning)]',
      'paid-in-full': 'text-[var(--ds-status-success)]',
      cancelled: 'text-[var(--ds-status-danger)]',
    };
    return colors[status] || 'text-[var(--ds-text-secondary)]';
  };

  const getStatusLabel = (status) => {
    if (status === 'paid-in-full') return 'Paid';
    if (status === 'pending') return 'Unpaid';
    return status.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  return (
    <TooltipProvider>
      <Layout>
      {/* Header */}
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                Purchases
              </h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                Manage your supplier purchases
              </p>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4 gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/tenant/dashboard')}
              >
                ← Back to Dashboard
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => navigate('/tenant/purchases/new')}
              >
                + New Purchase
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <FormLabel>Search</FormLabel>
              <Input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                placeholder="Search by purchase number, receipt number, or supplier..."
                className="h-8"
              />
            </div>
            <div>
              <FormLabel>Status</FormLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="h-8"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="received">Received</option>
                <option value="part-paid">Part Paid</option>
                <option value="paid-in-full">Paid in Full</option>
                <option value="cancelled">Cancelled</option>
              </Select>
            </div>
            <div>
              <FormLabel>Visibility</FormLabel>
              <Select
                value={visibilityFilter}
                onChange={(e) => {
                  setVisibilityFilter(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
                className="h-8"
              >
                <option value="active">Active</option>
                <option value="archived">Archived</option>
                <option value="deleted">Deleted</option>
              </Select>
            </div>
            {supplierFilter && (
              <div className="flex items-end gap-2">
                <span className="text-micro text-[var(--ds-text-muted)]">Supplier filter active</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setSupplierFilter(''); setPagination(prev => ({ ...prev, page: 1 })); }}
                >
                  Clear
                </Button>
              </div>
            )}
          </div>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 rounded-[var(--ds-radius-md)]">
            <p className="text-xs text-[var(--ds-status-danger)]">{error}</p>
          </div>
        )}

        {/* Purchases Table */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-xs text-gray-500">Loading purchases...</p>
          </div>
        ) : purchases.length === 0 ? (
          <Card className="text-center py-12">
            <p className="text-xs text-[var(--ds-text-muted)]">No purchases found</p>
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate('/tenant/purchases/new')}
              className="mt-4"
            >
              Create Your First Purchase
            </Button>
          </Card>
        ) : (
          <div>
            {selectedPurchaseIds.size > 0 && (
              <div className="mb-4 flex flex-wrap items-center gap-3 p-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={bulkActionInProgress}
                  className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Delete Selected
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenBulkPaymentModal}
                  disabled={bulkActionInProgress}
                  className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Update as paid
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleBulkArchive}
                  disabled={bulkActionInProgress}
                  className="border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Archive Selected
                </Button>
                <div className="min-w-[140px]">
                  <Select
                    className="h-8 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    defaultValue=""
                    disabled={bulkActionInProgress}
                  >
                    <option value="" disabled>More actions...</option>
                  </Select>
                </div>
              </div>
            )}
            {/* Static selection tally box - shown when 2+ purchases selected */}
            {selectedPurchaseIds.size >= 2 && (() => {
              const selectedPurchases = purchases.filter((p) => selectedPurchaseIds.has(p.id));
              const totalBalanceDue = selectedPurchases.reduce((sum, p) => sum + (Number(p.balanceDue) || 0), 0);
              return (
                <div
                  className="mb-3 px-4 py-3 inline-flex items-baseline gap-3 rounded-[var(--ds-radius-md)] bg-white dark:bg-gray-800 border border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]"
                  aria-live="polite"
                >
                  <p className="text-xs font-medium text-gray-900 dark:text-gray-100">
                    Total ({selectedPurchaseIds.size} Items)
                  </p>
                  <p className="text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                    {formatCurrency(totalBalanceDue)}
                  </p>
                </div>
              );
            })()}
            <Card className="overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow isHeader>
                    <TableHead className="w-10">
                      <input
                        type="checkbox"
                        checked={purchases.length > 0 && selectedPurchaseIds.size >= purchases.length}
                        onChange={toggleSelectAll}
                        disabled={purchases.length === 0}
                        className="rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-primary)] disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label="Select all"
                      />
                    </TableHead>
                    <TableHead>Receipt No.</TableHead>
                    <TableHead>Receipt Date</TableHead>
                    <TableHead>Supplier Name</TableHead>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead align="right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {purchases.map((purchase) => (
                    <TableRow key={purchase.id}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selectedPurchaseIds.has(purchase.id)}
                          onChange={() => toggleSelect(purchase.id)}
                          className="rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-[var(--ds-accent-primary)] focus:ring-[var(--ds-accent-primary)]"
                          aria-label={`Select ${purchase.receiptNumber || purchase.purchaseNum}`}
                        />
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => navigate(`/tenant/purchases/${purchase.id}?view=readonly`)}
                          className="font-medium text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)] hover:underline"
                        >
                          {purchase.receiptNumber || purchase.purchaseNum}
                        </button>
                      </TableCell>
                      <TableCell>
                        <span className="text-[var(--ds-text-primary)]">
                          {purchase.purchaseDate ? new Date(purchase.purchaseDate).toLocaleDateString('en-GB') : '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => purchase.supplier?.id && handleFilterOnSupplier(purchase.supplier.id)}
                          className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)] hover:underline text-left"
                        >
                          {purchase.supplier?.supplierName || '-'}
                        </button>
                      </TableCell>
                      <TableCell>
                        {(purchase.attachmentCount ?? 0) > 0 ? (
                          <Tooltip content="Has attached file">
                            <Paperclip className="h-4 w-4 text-[var(--ds-text-muted)]" aria-hidden />
                          </Tooltip>
                        ) : (
                          <span className="text-[var(--ds-text-muted)]">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="font-semibold tabular-nums text-[var(--ds-text-primary)]">
                          {formatCurrency(purchase.total)}
                        </div>
                        {purchase.balanceDue > 0 && (
                          <div className="text-[var(--ds-text-muted)] text-micro">
                            Due: {formatCurrency(purchase.balanceDue)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div
                          className="relative inline-block"
                          ref={(el) => { dropdownRefs.current[purchase.id] = el; }}
                        >
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setOpenDropdownId(openDropdownId === purchase.id ? null : purchase.id)}
                            className="w-[90px] min-w-[90px] h-7 px-2 text-xs border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 justify-between"
                          >
                            <span className={cn('font-semibold text-micro truncate', getStatusTextColor(purchase.status))}>
                              {getStatusLabel(purchase.status)}
                            </span>
                            <ChevronDown className="h-3.5 w-3.5 flex-shrink-0" />
                          </Button>
                          {openDropdownId === purchase.id && dropdownPosition && createPortal(
                            <div
                              ref={dropdownMenuRef}
                              className="fixed z-[9999] w-48 py-1 rounded-[var(--ds-radius-md)] bg-white dark:bg-gray-800 border border-[var(--ds-border-subtle)] shadow-lg"
                              role="menu"
                              style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                            >
                              <button
                                type="button"
                                onClick={() => handleDuplicate(purchase)}
                                disabled={duplicatingPurchaseId === purchase.id}
                                className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-[var(--ds-text-primary)] hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                role="menuitem"
                              >
                                <Copy className="h-4 w-4 text-[var(--ds-text-muted)]" />
                                {duplicatingPurchaseId === purchase.id ? 'Copying...' : 'Make a copy'}
                              </button>
                              {purchase.status !== 'paid-in-full' && purchase.balanceDue > 0 && (
                                <button
                                  type="button"
                                  onClick={() => handleLogPayment(purchase)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-[var(--ds-text-primary)] hover:bg-gray-100 dark:hover:bg-gray-700"
                                  role="menuitem"
                                >
                                  <CreditCard className="h-4 w-4 text-[var(--ds-text-muted)]" />
                                  Log a payment
                                </button>
                              )}
                              {purchase.supplier?.id && (
                                <button
                                  type="button"
                                  onClick={() => handleFilterOnSupplier(purchase.supplier.id)}
                                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-[var(--ds-text-primary)] hover:bg-gray-100 dark:hover:bg-gray-700"
                                  role="menuitem"
                                >
                                  <Filter className="h-4 w-4 text-[var(--ds-text-muted)]" />
                                  Filter on supplier
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => handleDeletePurchase(purchase)}
                                className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs text-[var(--ds-status-danger)] hover:bg-gray-100 dark:hover:bg-gray-700"
                                role="menuitem"
                              >
                                <Trash2 className="h-4 w-4" />
                                Delete
                              </button>
                            </div>,
                            document.body
                          )}
                        </div>
                      </TableCell>
                      <TableCell align="right">
                        <div className="flex items-center justify-end gap-1">
                          <Tooltip content="View">
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={() => navigate(`/tenant/purchases/${purchase.id}?view=readonly`)}
                              className="text-[var(--ds-text-secondary)] hover:text-[var(--ds-text-primary)]"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          <Tooltip content="Edit">
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={() => navigate(`/tenant/purchases/${purchase.id}`)}
                              className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </Tooltip>
                          <Tooltip content={purchase.isArchived ? 'Unarchive' : 'Archive'}>
                            <Button
                              variant="icon"
                              size="sm"
                              onClick={() => handleArchivePurchase(purchase.id, purchase.isArchived)}
                              className="text-[var(--ds-text-secondary)] hover:text-[var(--ds-text-primary)]"
                            >
                              {purchase.isArchived ? (
                                <ArchiveRestore className="h-4 w-4" />
                              ) : (
                                <Archive className="h-4 w-4" />
                              )}
                            </Button>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>

            {/* Pagination */}
              {pagination.totalPages > 1 && (
                <Card className="mt-6">
                  <CardContent className="px-4 py-3 sm:px-6">
                    <div className="flex flex-1 justify-between sm:hidden">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={pagination.page === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={pagination.page >= pagination.totalPages}
                      >
                        Next
                      </Button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                      <div>
                        <p className="text-xs text-[var(--ds-text-secondary)]">
                          Showing <span className="font-medium">{(pagination.page - 1) * pagination.limit + 1}</span> to{' '}
                          <span className="font-medium">
                            {Math.min(pagination.page * pagination.limit, pagination.total)}
                          </span>{' '}
                          of <span className="font-medium">{pagination.total}</span> results
                        </p>
                      </div>
                      <div>
                        <nav className="relative z-0 inline-flex rounded-[var(--ds-radius-md)] shadow-sm -space-x-px" aria-label="Pagination">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                            disabled={pagination.page === 1}
                            className="rounded-l-[var(--ds-radius-md)] rounded-r-none"
                          >
                            Previous
                          </Button>
                          {[...Array(pagination.totalPages)].map((_, i) => (
                            <Button
                              key={i + 1}
                              variant={pagination.page === i + 1 ? 'primary' : 'outline'}
                              size="sm"
                              onClick={() => setPagination(prev => ({ ...prev, page: i + 1 }))}
                              className="rounded-none"
                            >
                              {i + 1}
                            </Button>
                          ))}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                            disabled={pagination.page >= pagination.totalPages}
                            className="rounded-r-[var(--ds-radius-md)] rounded-l-none"
                          >
                            Next
                          </Button>
                        </nav>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
          </div>
        )}

        {/* Confirm delete modal (for purchases without payments, or final confirm after payment choice) */}
        <Dialog.Root
          open={!!confirmDeletePurchase}
          onOpenChange={(open) => {
            if (!open && !deleteInProgress) setConfirmDeletePurchase(null);
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-[10000] bg-black/50" />
            <Dialog.Content
              className="fixed top-1/2 left-1/2 z-[10001] w-full max-w-md -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-white dark:bg-gray-800 shadow-lg"
              onPointerDownOutside={(e) => deleteInProgress && e.preventDefault()}
              onEscapeKeyDown={(e) => deleteInProgress && e.preventDefault()}
            >
              <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-3">
                <Dialog.Title className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  Delete purchase
                </Dialog.Title>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Are you sure you want to delete this purchase? This will soft-delete the purchase and it can be restored if needed.
                </p>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => !deleteInProgress && setConfirmDeletePurchase(null)}
                  disabled={deleteInProgress}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
                  onClick={handleConfirmDelete}
                  disabled={deleteInProgress}
                >
                  {deleteInProgress ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        {/* Delete purchase: payment disposition modal (when purchase has payments) */}
        <Dialog.Root
          open={!!deleteModalPurchase}
          onOpenChange={(open) => {
            if (!open && !deleteInProgress) setDeleteModalPurchase(null);
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-[10000] bg-black/50" />
            <Dialog.Content
              className="fixed top-1/2 left-1/2 z-[10001] w-full max-w-md -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-white dark:bg-gray-800 shadow-lg"
              onPointerDownOutside={(e) => deleteInProgress && e.preventDefault()}
              onEscapeKeyDown={(e) => deleteInProgress && e.preventDefault()}
            >
              <div className="border-b border-gray-200 dark:border-gray-700 px-4 py-3">
                <Dialog.Title className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  What would you like to do with the associated payment?
                </Dialog.Title>
              </div>
              <div className="p-4 space-y-3">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  This purchase has payment(s) recorded. Choose how to handle them before deleting the purchase.
                </p>
                <div className="flex flex-col gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start gap-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    onClick={() => handlePaymentDispositionChoice('delete')}
                    disabled={deleteInProgress}
                  >
                    <Banknote className="h-4 w-4 text-[var(--ds-text-muted)]" />
                    Delete payment
                  </Button>
                  <p className="text-xs text-gray-500 dark:text-gray-400 px-1">
                    Removes payment and bank records; supplier balance is restored for the paid amount.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start gap-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    onClick={() => handlePaymentDispositionChoice('convert_to_credit')}
                    disabled={deleteInProgress}
                  >
                    <Wallet className="h-4 w-4 text-[var(--ds-text-muted)]" />
                    Convert to supplier credit
                  </Button>
                  <p className="text-xs text-gray-500 dark:text-gray-400 px-1">
                    Removes payment and bank records; the paid amount stays as credit with the supplier.
                  </p>
                </div>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => !deleteInProgress && setDeleteModalPurchase(null)}
                  disabled={deleteInProgress}
                >
                  Cancel
                </Button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>

      {/* Log Payment Slide-Over */}
      {paymentModalPurchase && (
        <SlideOver
          open={!!paymentModalPurchase}
          onOpenChange={(open) => !open && setPaymentModalPurchase(null)}
          title="Log a payment"
          width="w-full max-w-lg"
        >
          <PurchasePaymentForm
            purchaseId={paymentModalPurchase.id}
            balanceDue={paymentModalPurchase.balanceDue ?? 0}
            onSuccess={handlePaymentRecorded}
            onCancel={() => setPaymentModalPurchase(null)}
          />
        </SlideOver>
      )}

      {/* Bulk Payment Modal */}
      {showBulkPaymentModal && (
        <SlideOver
          open={showBulkPaymentModal}
          onOpenChange={(open) => !open && setShowBulkPaymentModal(false)}
          title="Log payment (bulk)"
          width="w-full max-w-xl"
        >
          <BulkPurchasePaymentModal
            purchases={purchases.filter((p) => selectedPurchaseIds.has(p.id))}
            onSuccess={handleBulkPaymentSuccess}
            onCancel={handleBulkPaymentCancel}
          />
        </SlideOver>
      )}
    </Layout>
    </TooltipProvider>
  );
};

export default Purchases;

